import React, { useEffect, useState } from "react";
import axios from "axios";
import style from "./JForm.module.css";

/**
 * DonationForm.jsx - single-seva version
 * - Replaced many sevas with one always-open seva "Anna Daan Seva"
 * - Preset amount buttons + custom amount input
 * - Keeps all payment, validation, Google Sheets storage & other features unchanged
 */

const PRESET_AMOUNTS = [
  30000,
  15000,
  6000,
  3000,
  1500
];

const AMOUNTS = [10000, 5000, 2500, 1000]; // kept (unused) for compatibility if needed
const BHAGVATGITAAMOUNTS = [
  { amount: 3000, text: "10 Bhagavad Gita" },
  { amount: 6000, text: "20 Bhagavad Gita" },
  { amount: 30000, text: "100 Bhagavad Gita" },
];
const FEED_AMOUNTS = [1500, 3000, 6000, 15000, 30000];
const ANNADAN_SUPPORT_AMOUNTS = [3000, 5000];

const DonationForm = () => {
  const [formData, setFormData] = useState({
    // single always-open seva
    sevaDate: "",
    selectedSevaAmount: null,
    customSevaAmount: "",

    // Global donate-as-you-wish
    donateAsYouWish: "",

    // Personal details
    donorName: "",
    email: "",
    mobile: "",
    dob: "",

    // Certificates / logistics
    receive80G: false,
    panNumber: "",
    receiveMahaPrasadam: false,

    // Address for prasadam / 80G
    mahaPrasadamDetails: {
      doorNumber: "",
      houseName: "",
      streetName: "",
      area: "",
      country: "",
      state: "",
      city: "",
      pincode: "",
    },
  });

  const [errors, setErrors] = useState({});
  const [show80GDetails, setShow80GDetails] = useState(false);
  const [showMahaPrasadamDetails, setShowMahaPrasadamDetails] = useState(false);

  useEffect(() => {
    const loadRazorpay = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve(true);
          return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadRazorpay();
  }, []);

  // ---------------- Helpers ----------------
  const formatINR = (n) => (n || n === 0 ? n.toLocaleString("en-IN") : "");

  const parseNumber = (v) => {
    const parsed = parseFloat(v || "0");
    return isNaN(parsed) ? 0 : parsed;
  };

  // compute single-seva amount: prefer selected preset else parsed custom
  const getSevaAmount = () => {
    if (formData.selectedSevaAmount) return formData.selectedSevaAmount;
    const parsed = parseNumber(formData.customSevaAmount);
    return parsed;
  };

  const getTotalAmount = () => {
    const seva = getSevaAmount();
    const globalDonate = parseNumber(formData.donateAsYouWish);
    return seva + globalDonate;
  };

  const selectedSevasSummary = () => {
    const parts = [];
    const sevaAmt = getSevaAmount();
    if (sevaAmt > 0) parts.push(`Anna Daan Seva: ₹${formatINR(sevaAmt)}`);
    const globalDonate = parseNumber(formData.donateAsYouWish);
    if (globalDonate > 0) parts.push(`Donate as you wish: ₹${formatINR(globalDonate)}`);
    if (formData.sevaDate) parts.push(`Seva Date: ${formData.sevaDate}`);
    return parts.join(" | ") || "Donation Payment";
  };

  // ---------------- Handlers ----------------
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      // update nested address fields if name matches
      if (name in prev.mahaPrasadamDetails) {
        return {
          ...prev,
          mahaPrasadamDetails: {
            ...prev.mahaPrasadamDetails,
            [name]: value,
          },
        };
      }

      return {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  // select preset amount; toggle off if same pressed; clears custom
  const handleSevaAmountSelect = (amount) => {
    setFormData((prev) => ({
      ...prev,
      selectedSevaAmount: prev.selectedSevaAmount === amount ? null : amount,
      customSevaAmount: "",
    }));
  };

  // custom input for single seva: set custom and deselect preset
  const handleCustomSevaChange = (value) => {
    // allow only numbers
    const cleaned = value.replace(/[^0-9.]/g, "");
    setFormData((prev) => ({
      ...prev,
      customSevaAmount: cleaned,
      selectedSevaAmount: null,
    }));
  };

  const handle80GCheckboxChange = (e) => {
    setShow80GDetails(e.target.checked);
    handleInputChange(e);
  };

  const handleMahaPrasadamCheckboxChange = (e) => {
    setShowMahaPrasadamDetails(e.target.checked);
    handleInputChange(e);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.donorName) newErrors.donorName = "Donor name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.mobile) newErrors.mobile = "Mobile number is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";

    if (formData.receive80G && !formData.panNumber)
      newErrors.panNumber = "PAN number is required for 80G certificate";

    if (getTotalAmount() <= 0)
      newErrors.total =
        "Please select the seva amount or enter a custom amount.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------------- Submit + Payment ----------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const totalAmount = getTotalAmount();
    let isFormDataStored = false;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/payment/create-order`,
        {
          amount: totalAmount,
          currency: "INR",
        }
      );

      const order = response.data;
      const sevaDescription = selectedSevasSummary() || "Donation Payment";

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: totalAmount * 100,
        currency: "INR",
        name: "ISKCON Donation",
        description: sevaDescription,
        order_id: order.id,
        notes: {
          donorName: formData.donorName,
          donorPhoneNumber: formData.mobile,
          donorEmail: formData.email,
        },
        handler: async function (response) {
          try {
            const verificationResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}/api/payment/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                donationDetails: { ...formData, amount: totalAmount },
              }
            );

            if (verificationResponse.data.success) {
              alert("Payment Successful!");
              if (!isFormDataStored) {
                await storeFormData("Payment Successful");
                isFormDataStored = true;
              }
            }
          } catch (err) {
            console.error(err);
            alert("Payment verification failed");
            if (!isFormDataStored) {
              await storeFormData("Payment Failed");
              isFormDataStored = true;
            }
          }
        },
        prefill: {
          name: formData.donorName,
          email: formData.email,
          contact: formData.mobile,
        },
        theme: { color: "#C61D21" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

      paymentObject.on("payment.failed", async function (response) {
        alert("Payment Failed: " + response.error.description);
        if (!isFormDataStored) {
          await storeFormData("Payment Failed");
          isFormDataStored = true;
        }
      });
    } catch (error) {
      console.error("Error initiating payment:", error);
      alert("Failed to initiate payment. Please try again.");
      if (!isFormDataStored) {
        await storeFormData("Payment Failed");
        isFormDataStored = true;
      }
    }
  };

  // ---------------- Store to Google Sheets ----------------
  const storeFormData = async (paymentStatus) => {
    const totalDonationAmount = getTotalAmount();

    const formDataToSend = new URLSearchParams();
    formDataToSend.append("paymentStatus", paymentStatus);

    // single seva fields
    formDataToSend.append("sevaName", "Anna Daan Seva");
    formDataToSend.append("sevaDate", formData.sevaDate || "");
    formDataToSend.append(
      "sevaAmount",
      getSevaAmount()
    );

    // Global donate-as-you-wish field
    formDataToSend.append(
      "donateAsYouWish",
      parseNumber(formData.donateAsYouWish)
    );

    // Totals & personal
    formDataToSend.append("totalDonationAmount", totalDonationAmount);
    formDataToSend.append("donorName", formData.donorName);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("mobile", formData.mobile);
    formDataToSend.append("dob", formData.dob);

    // Certificates / address
    formDataToSend.append("receive80G", formData.receive80G ? "Yes" : "No");
    formDataToSend.append(
      "panNumber",
      formData.receive80G ? formData.panNumber : ""
    );
    formDataToSend.append(
      "receiveMahaPrasadam",
      formData.receiveMahaPrasadam ? "Yes" : "No"
    );

    const addr = formData.mahaPrasadamDetails || {};
    formDataToSend.append("doorNumber", addr.doorNumber || "");
    formDataToSend.append("houseName", addr.houseName || "");
    formDataToSend.append("streetName", addr.streetName || "");
    formDataToSend.append("area", addr.area || "");
    formDataToSend.append("country", addr.country || "");
    formDataToSend.append("state", addr.state || "");
    formDataToSend.append("city", addr.city || "");
    formDataToSend.append("pincode", addr.pincode || "");

    try {
      const response = await fetch(
        "https://script.google.com/macros/s/AKfycbzx6xwEnTzDGQ2wE7el1Js3p-qQro7f_AGtfX9XJm4hmv_HjU3EsxJVjTXmaeD50NKU/exec",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: formDataToSend.toString(),
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");
      console.log("✅ Data stored successfully in Google Sheets");
    } catch (error) {
      console.warn("⚠️ Failed to store data in Google Sheets:", error);
      // Do NOT block payment
    }

    return true;
  };

  // ---------------- JSX (single always-open seva) ----------------
  return (
    <div className="JForm">
      <div className="xl:flex xl:justify-center xl:pt-[3.5rem] mb-8 md:mt-4 mt-8">
        <div className="donationDetail bg-[#C61D21] py-10 px-2 rounded-3xl xl:h-full xl:p-8 xl:max-w-[800px] w-full">
          <form onSubmit={handleSubmit} className="text-white">
            <h2 className="text-center text-2xl xl:text-4xl">
              Donation Details
            </h2>

            {/* ---------------- Single always-open Seva: Anna Daan Seva ---------------- */}
            <div className="mt-6">
              <div className="p-3 rounded-xl bg-[#F9E7D0] font-semibold text-gray-800 text-lg">
                Anna Daan Seva
              </div>

              <div className="grid grid-cols-2 gap-2 mt-3">
                {PRESET_AMOUNTS.map((amount) => (
                  <button
                    type="button"
                    key={`seva-${amount}`}
                    onClick={() => handleSevaAmountSelect(amount)}
                    className={`col-span-1 font-semibold p-3 rounded-xl text-gray-700 ${formData.selectedSevaAmount === amount
                      ? "bg-[#fac785]"
                      : "bg-[#F9E7D0]"
                      }`}
                  >
                    ₹ {formatINR(amount)}{amount === 30000 ? " (Feed 1000 People)" : amount === 15000 ? " (Feed 500 People)" : amount === 6000 ? " (Feed 200 People)" : amount === 3000 ? " (Feed 100 People)" : amount === 1500 ? " (Feed 50 People)" : ""}
                  </button>
                ))}

                <input
                  type="text"
                  name="customSevaAmount"
                  placeholder="₹ Donate as you wish"
                  value={formData.customSevaAmount}
                  onChange={(e) => handleCustomSevaChange(e.target.value)}
                  className="col-span-2 p-3 border rounded-xl my-1 mx-1 text-black"
                />
              </div>

              <div className="mt-3 ml-0">
                <input
                  type="date"
                  name="sevaDate"
                  value={formData.sevaDate}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-xl my-1 text-black"
                  placeholder="Seva Date"
                />
              </div>
            </div>

            {/* ---------------- Global Donate As You Wish ---------------- */}
            <div className="mt-6">
              <label className="flex items-center gap-3 p-3 rounded-xl bg-[#F9E7D0] font-semibold text-gray-800">
                <span>Donate as you wish</span>
              </label>
              <div className="mt-2 ml-4">
                <input
                  type="number"
                  name="donateAsYouWish"
                  placeholder="₹ Enter amount"
                  value={formData.donateAsYouWish}
                  onChange={handleInputChange}
                  className="w-full p-3 border rounded-xl my-1 text-black"
                  min="0"
                />
                <p className="text-xs mt-1 text-white">This amount will be added to your total donation.</p>
              </div>
            </div>

            {/* Personal Details */}
            <div className="innerForm mt-6">
              <h3 className="text-center">Personal Details</h3>
              <label className="xl:w-full block mt-2">
                <input
                  type="text"
                  name="donorName"
                  value={formData.donorName}
                  onChange={handleInputChange}
                  placeholder="Donor Name"
                  className="w-full p-3 rounded-lg text-black"
                />
                {errors.donorName && (
                  <span className="block mt-1 ml-2 text-sm">
                    {errors.donorName}
                  </span>
                )}
              </label>

              <label className="xl:w-full block mt-3">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email ID"
                  className="w-full p-3 rounded-lg text-black"
                />
                {errors.email && (
                  <span className="block mt-1 ml-2 text-sm">{errors.email}</span>
                )}
              </label>

              <div className="flex gap-2 mt-3">
                <label className="flex-1">
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder="Mobile Number"
                    className="w-full p-[10px]  text-black"
                  />
                  {errors.mobile && (
                    <span className="block mt-1 ml-2 text-sm">{errors.mobile}</span>
                  )}
                </label>
                <label className="flex-1">
                  <input
                    type="text"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-lg text-black"
                    placeholder="Date Of Birth"
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => (e.target.type = "text")}
                  />
                  {errors.dob && (
                    <span className="block mt-1 ml-2 text-sm">{errors.dob}</span>
                  )}
                </label>
              </div>
            </div>

            <p className="text-white mt-2 text-[10px]">
              (Sankalpa and Archana will be performed for you on your birthday)
            </p>

            <p className="text-white mt-1 text-xs">
              By continuing, you are agreeing to{" "}
              <a className="underline text-[#EEF9A3]">
                Terms of Use and Privacy Policy
              </a>
            </p>

            {/* Certificates & Logistics */}
            <div className="px-3 tandd mt-8">
              <label className="block">
                <input
                  type="checkbox"
                  name="receive80G"
                  checked={formData.receive80G}
                  onChange={handle80GCheckboxChange}
                  style={{ accentColor: "blue" }}
                />
                <span className="ml-2">
                  I would like to receive 80(G) Certificate
                </span>
              </label>

              {show80GDetails && (
                <div className="mb-3 mt-2">
                  <p className="text-white text-[10px] text-left ml-2">
                    Finance Act 2021 mandates uploading donation details for
                    80(G) deductions. Ensure PAN and address details are
                    provided.
                  </p>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData.panNumber}
                    onChange={handleInputChange}
                    placeholder="PAN Number *"
                    className="w-full bg-white text-black px-4 py-2 rounded-lg outline-none focus:ring-2 focus:ring-yellow-400"
                    required
                  />

                  {/* Address inputs for 80G */}
                  {[
                    "doorNumber",
                    "houseName",
                    "streetName",
                    "area",
                    "country",
                    "state",
                    "city",
                    "pincode",
                  ].map((key) => (
                    <input
                      key={`80g-${key}`}
                      type="text"
                      name={key}
                      placeholder={
                        {
                          doorNumber: "Door Number",
                          houseName: "House / Apartment / Building Name",
                          streetName: "Street Name",
                          area: "Location / Area",
                          country: "Country",
                          state: "State",
                          city: "City",
                          pincode: "Pincode",
                        }[key]
                      }
                      className="w-full bg-white text-black px-4 py-2 rounded-lg mt-2"
                      value={formData.mahaPrasadamDetails[key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          mahaPrasadamDetails: {
                            ...prev.mahaPrasadamDetails,
                            [key]: e.target.value,
                          },
                        }))
                      }
                    />
                  ))}
                </div>
              )}

              <label className="block mt-4">
                <input
                  type="checkbox"
                  name="receiveMahaPrasadam"
                  checked={formData.receiveMahaPrasadam}
                  onChange={handleMahaPrasadamCheckboxChange}
                  style={{ accentColor: "blue" }}
                />
                <span className="ml-2">
                  I would like to receive Maha prasadam (Only within India)
                </span>
              </label>

              {showMahaPrasadamDetails && (
                <div className="mt-3">
                  {[
                    "doorNumber",
                    "houseName",
                    "streetName",
                    "area",
                    "country",
                    "state",
                    "city",
                    "pincode",
                  ].map((key) => (
                    <input
                      key={`prasadam-${key}`}
                      type="text"
                      name={key}
                      placeholder={
                        {
                          doorNumber: "Door Number",
                          houseName: "House / Apartment / Building Name",
                          streetName: "Street Name",
                          area: "Location / Area",
                          country: "Country",
                          state: "State",
                          city: "City",
                          pincode: "Pincode",
                        }[key]
                      }
                      className="w-full bg-white text-black px-4 py-2 rounded-lg mt-2"
                      value={formData.mahaPrasadamDetails[key]}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          mahaPrasadamDetails: {
                            ...prev.mahaPrasadamDetails,
                            [key]: e.target.value,
                          },
                        }))
                      }
                    />
                  ))}
                </div>
              )}
            </div>

            {errors.total && (
              <p className="text-yellow-200 text-sm mt-4">{errors.total}</p>
            )}

            <div className="flex justify-center mt-8">
              <button
                type="submit"
                className="bg-[#FAE29C] text-black px-12 py-3 rounded-2xl font-bold"
              >
                Donate{" "}
                {getTotalAmount() > 0 ? `₹ ${formatINR(getTotalAmount())}` : ""}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DonationForm;
